import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LanguageCode } from '../../../../../amorg-common';
import { LoadingPageOverlay } from '../../../../../designSystem/LoadingPageOverlay';
import { stxt } from '../../../../../SurveyTxt';
import { getAnswerStatus, submitSegments, submitSelection } from '../../../../../api/surveySubmissionClientApi';
import { SelectionState, SurveyAnswerStatus } from '../../../../../amorg-common';
import { SurveySegment } from '../../../../../amorg-common';
import { SetQuestionSelection } from '../../_components/SetQuestionSelection';
import { SetSegments } from '../../_components/SetSegments';
import { SurveyDoneView } from '../../_components/SurveyDone';
import { SurveyIntroductionView } from '../../_components/SurveyIntroduction';
import { parseSiteScopedRouteParam } from '../../../../../utils/parseSiteScopedRouteParam';
import { SurveyAnswerPageWrapper } from '../../_components/SurveyAnswerPageWrapper';
import { SurveyOuterCard } from '../../_components/SurveyCard';
import { colors } from '../../../../../designSystem/colors/color';
import { Flex } from '../../../../../designSystem/compositeComponents/FlexHelpers';
import { Icon } from '../../../../../designSystem/icons/Icons';

export function SurveyAnswerPage(): JSX.Element {
    const params = useParams<{ surveyIdSiteId: string, answerId: string }>();
    const surveyRouteParams = parseSiteScopedRouteParam(params.surveyIdSiteId);
    
    const [availableLanguages, setAvailableLanguages] = useState<LanguageCode[]>(['da']);
    const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>('da');
    const [answerStatus, setAnswerStatus] = useState<SurveyAnswerStatus | null>(null);

    useEffect(() => {
        fetchStatus();
    }, []);

    // Fetch state from the server. With no target the server returns the
    // default resume stage; an explicit target requests a concrete screen
    // ('intro' | 'segmentations' | 'question:<index>').
    async function fetchStatus(target?: string) {
        const status = await getAnswerStatus(
            surveyRouteParams.siteId,
            surveyRouteParams.value,
            params.answerId!,
            target
        );
        if (status.status === 'ok') {
            setAvailableLanguages(status.languages);
            setSelectedLanguage(prev =>
                status.languages.includes(prev) ? prev : status.defaultLanguage
            );
        }
        setAnswerStatus(status);
    }

    async function handleSubmitSegments(segments: SurveySegment[]) {
        await submitSegments(
            surveyRouteParams.siteId,
            surveyRouteParams.value,
            params.answerId!,
            segments
        );
        // The server resolves the next stage from the freshly written lastAction.
        await fetchStatus();
    }

    async function handleSubmitSelection(absoluteQuestionIndex: number, values: string[], comment?: string) {
        await submitSelection(
            surveyRouteParams.siteId,
            surveyRouteParams.value,
            params.answerId!,
            absoluteQuestionIndex,
            values,
            comment
        );
        // The server resolves the next stage from the freshly written lastAction.
        await fetchStatus();
    }

    // Navigation helpers, derived from the selection state returned by the server.
    function previousActiveIndex(selectionState: SelectionState, fromIndex: number): number {
        for (let i = fromIndex - 1; i >= 0; i--) {
            if (selectionState.selections[i].state !== 'skipped')
                return i;
        }
        return -1;
    }

    function hasSegmentations(selectionState: SelectionState): boolean {
        return selectionState.segmentations !== 'not-applicable';
    }

    function activeQuestionIndex(selectionState: SelectionState, absoluteIndex: number): number {
        let count = 0;
        for (let i = 0; i < absoluteIndex; i++) {
            if (selectionState.selections[i]?.state !== 'skipped')
                count++;
        }
        return count;
    }

    function totalActive(selectionState: SelectionState): number {
        return selectionState.selections.filter(s => s.state !== 'skipped').length;
    }

    // The first step after the introduction (or when starting over from a
    // completed answer): segmentations when the survey has them, otherwise the
    // first active question.
    function firstStepTarget(): string | undefined {
        if (answerStatus?.status !== 'ok')
            return undefined;
        if (hasSegmentations(answerStatus.selectionState))
            return 'segmentations';
        const firstIndex = answerStatus.selectionState.selections.findIndex(s => s.state !== 'skipped');
        return firstIndex !== -1 ? `question:${firstIndex}` : undefined;
    }

    // Start from the introduction.
    function handleStart() {
        const target = firstStepTarget();
        if (target)
            fetchStatus(target);
    }

    // Back-navigation from a question: previous active question, then the
    // segmentation step, then the introduction.
    async function handlePreviousQuestionClicked(currentAbsoluteQuestionIndex: number) {
        if (answerStatus?.status !== 'ok')
            return;

        const previousIndex = previousActiveIndex(answerStatus.selectionState, currentAbsoluteQuestionIndex);
        if (previousIndex !== -1)
            await fetchStatus(`question:${previousIndex}`);
        else if (hasSegmentations(answerStatus.selectionState))
            await fetchStatus('segmentations');
        else
            await fetchStatus('intro');
    }

    // Start over from a completed answer.
    async function handleChangeAnswer() {
        const target = firstStepTarget();
        if (target)
            await fetchStatus(target);
    }

    const txt = stxt(selectedLanguage);

    let view = <LoadingPageOverlay loading={true} />;

    if (answerStatus) {
        if (answerStatus.status === 'unavailable' || answerStatus.status === 'error')
            view = <SurveyOuterCard>
                        <Flex gap={16} alignItems="center" justifyContent="center">
                            <Icon name="status_error" color={colors.icon.error} size="medium" />
                            <div>{txt.overview_surveyClosed}</div>
                        </Flex>
                    </SurveyOuterCard>;

        else if (answerStatus.stageData.type === 'intro')
            view = <SurveyIntroductionView
                        txt={txt}
                        selectedLanguage={selectedLanguage}
                        intro={answerStatus.stageData.intro}
                        defaultLanguage={answerStatus.defaultLanguage}
                        questionCount={answerStatus.selectionState.selections.length}
                        hasSegmentations={hasSegmentations(answerStatus.selectionState)}
                        onClickStart={handleStart} />;

        else if (answerStatus.stageData.type === 'segmentations')
            view = <SetSegments
                        txt={txt}
                        selectedLanguage={selectedLanguage}
                        segmentations={answerStatus.stageData.segmentations}
                        questionCount={answerStatus.selectionState.selections.length}
                        defaultLanguage={answerStatus.defaultLanguage}
                        isCompleted={answerStatus.stageData.hasPreviousSegmentSelection}
                        onPreviousClicked={() => fetchStatus('intro')}
                        onSubmitSegments={handleSubmitSegments} />;

        else if (answerStatus.stageData.type === 'question')
            view = <SetQuestionSelection
                        txt={txt}
                        selectedLanguage={selectedLanguage}
                        absoluteQuestionIndex={answerStatus.stageData.absoluteQuestionIndex}
                        question={answerStatus.stageData.question}
                        themeName={answerStatus.stageData.themeName}
                        themeTranslations={answerStatus.stageData.themeTranslations}
                        activeQuestionIndex={activeQuestionIndex(answerStatus.selectionState, answerStatus.stageData.absoluteQuestionIndex)}
                        totalActive={totalActive(answerStatus.selectionState)}
                        isFirst={activeQuestionIndex(answerStatus.selectionState, answerStatus.stageData.absoluteQuestionIndex) === 0}
                        isCompleted={answerStatus.stageData.isCompleted}
                        defaultLanguage={answerStatus.defaultLanguage}
                        hasSegmentations={hasSegmentations(answerStatus.selectionState)}
                        onPreviousClicked={handlePreviousQuestionClicked}
                        onSubmitSelection={handleSubmitSelection} />;

        else if (answerStatus.stageData.type === 'done')
            view = <SurveyDoneView txt={txt} answerId={params.answerId || ''} onChangeAnswer={handleChangeAnswer} />;
    }

    return (
        <SurveyAnswerPageWrapper
            siteId={surveyRouteParams.siteId}
            availableLanguages={availableLanguages}
            selectedLanguage={selectedLanguage}
            onSelectedLanguageChange={(languageCode) => setSelectedLanguage(languageCode)}>

            {view}

        </SurveyAnswerPageWrapper>
    );
}